import { CharObject } from "../js/objects/charobject";
import { MobObject } from "../js/objects/mobobject";
import { NpcObject } from "../js/objects/npcobject";
import { ItemObject } from "../js/objects/itemobject";
import { Font } from "../js/util/font";

function _measureText(font: any, text: any) {
    const div = document.createElement("div");
    div.innerHTML = "";
    div.appendChild(document.createTextNode(text));
    div.style.position = "absolute";
    div.style.top = "-9999px";
    div.style.left = "-9999px";
    div.style.fontFamily = font.family;
    div.style.fontWeight = font.weight;
    div.style.fontSize = font.size + "pt";
    document.body.appendChild(div);
    const size = { x: div.offsetWidth, y: div.offsetHeight };
    document.body.removeChild(div);

    if ((font.style & Font.STYLE.OUTLINE) !== 0) {
        size.x = Math.round((size.x + 3) / 2) * 2;
        size.y = Math.round((size.y + 3) / 2) * 2;
    }

    return size;
}

function _drawFont(g: any, x: any, y: any, font: any, text: any, color: any) {
    g.font = font.weight + " " + font.size + "pt " + font.family;

    if ((font.style & Font.STYLE.OUTLINE) !== 0) {
        g.fillStyle = "black";
        g.fillText(text, x + 0, y + 1);
        g.fillText(text, x + 1, y + 0);
        g.fillText(text, x + 0, y + 0);
        g.fillText(text, x + 2, y + 1);
        g.fillText(text, x + 1, y + 2);
        g.fillText(text, x + 2, y + 2);
    }

    g.fillStyle = color;
    g.fillText(text, x + 1, y + 1);
}

function _drawCharNamePlate(g: any, gameObject: any) {
    const font = Font.getFont(Font.FONT.NORMAL_OUTLINE);
    let name = gameObject.name;

    const color = gameObject.selected ? "orange" : "white";

    const size = _measureText(font, name);
    _drawFont(g, 128 / 2 - size.x / 2, 64 + 10 - size.y, font, name, color);
}

// Color tiers by (my level - monster level), matching the classic MMO
// "con color" convention: greener/grayer means the monster is safely below
// your level, redder/purpler means it's dangerously above it.
function _levelDiffColor(myLevel: any, mobLevel: any) {
    if (typeof myLevel !== "number" || typeof mobLevel !== "number") {
        return "yellow";
    }
    const diff = myLevel - mobLevel;
    if (diff >= 22) return "gray";
    if (diff >= 15) return "lightblue";
    if (diff >= 9) return "blue";
    if (diff >= 4) return "green";
    if (diff >= -3) return "yellow";
    if (diff >= -9) return "orange";
    if (diff >= -15) return "red";
    if (diff >= -22) return "pink";
    return "purple";
}

const HP_BAR_WIDTH = 78;
const HP_BAR_HEIGHT = 9;

function _drawHealthBar(g: any, gameObject: any, y: number, displayedHp: number) {
    const barWidth = HP_BAR_WIDTH;
    const barHeight = HP_BAR_HEIGHT;
    const x = 128 / 2 - barWidth / 2;

    let healthPercent = 1.0;
    if (gameObject.maxHp !== undefined && gameObject.maxHp > 0) {
        healthPercent = Math.max(0, Math.min(1, displayedHp / gameObject.maxHp));
    }

    // Transparent gray background with a small border.
    g.fillStyle = "rgba(60, 60, 60, 0.55)";
    g.fillRect(x, y, barWidth, barHeight);
    g.strokeStyle = "rgba(0, 0, 0, 0.8)";
    g.lineWidth = 1;
    g.strokeRect(x, y, barWidth, barHeight);

    g.fillStyle = "#dd2222";
    g.fillRect(x, y, barWidth * healthPercent, barHeight);

    return barHeight;
}

function _drawNpcNamePlate(g: any, gameObject: any, displayedHp: number) {
    let topText, bottomText;
    const name = gameObject.name;
    const idx = name.indexOf("]");
    if (idx !== -1) {
        topText = name.substr(0, idx + 1).trim();
        bottomText = name.substr(idx + 1).trim();
    } else {
        topText = "";
        bottomText = name;
    }

    if (gameObject.selected) {
        bottomText = "|> " + bottomText + " <|";
    }

    const isMob = !(gameObject instanceof NpcObject) && gameObject instanceof MobObject;
    const nameColor = isMob ? _levelDiffColor(MC && MC.level, gameObject.level) : "white";

    // The canvas is only 64px tall: pin the mob HP bar to its bottom edge and
    // lift the name just above it, otherwise the bar lands off-canvas.
    const barY = 64 - HP_BAR_HEIGHT - 1;
    let curY = 64 + 10 - (isMob ? HP_BAR_HEIGHT + 3 : 0);

    if (bottomText) {
        let font = Font.getFont(Font.FONT.NORMAL_OUTLINE);
        let size = _measureText(font, bottomText);
        curY -= size.y - 3;
        _drawFont(g, 128 / 2 - size.x / 2, curY, font, bottomText, nameColor);
    }

    if (topText) {
        const font = Font.getFont(Font.FONT.NORMAL_OUTLINE);
        const size = _measureText(font, topText);
        curY -= size.y - 3;
        _drawFont(g, 128 / 2 - size.x / 2, curY, font, topText, "#ffceae");
    }

    if (isMob) {
        _drawHealthBar(g, gameObject, barY, displayedHp);
    }
}

function _drawItemNamePlate(g: any, gameObject: any) {
    const font = Font.getFont(Font.FONT.NORMAL_OUTLINE);
    const name = gameObject.name;
    const size = _measureText(font, name);
    _drawFont(g, 128 / 2 - size.x / 2, 64 + 10 - size.y, font, name, "#ffceae");
}

function _drawNamePlate(g: any, gameObject: any, displayedHp: number) {
    if (gameObject instanceof CharObject) {
        _drawCharNamePlate(g, gameObject);
    } else if (gameObject instanceof MobObject) {
        _drawNpcNamePlate(g, gameObject, displayedHp);
    } else if (gameObject instanceof ItemObject) {
        _drawItemNamePlate(g, gameObject);
    }
}

ui.createNamePlate = (gameObject: any) => {
    const bitmap = document.createElement("canvas");
    const g = bitmap.getContext("2d");
    bitmap.width = 128;
    bitmap.height = 64;
    const texture = new THREE.Texture(bitmap);

    let displayedHp = gameObject.hp;
    let animFrame: number | null = null;

    const redraw = () => {
        g.clearRect(0, 0, 128, 64);
        _drawNamePlate(g, gameObject, displayedHp);
        texture.needsUpdate = true;
    };

    // ponytail: naive per-frame ease (not time-based) toward the real HP;
    // fine for a short bar tween, revisit with a delta-time lerp if the
    // frame rate ever varies enough to matter.
    const animateHp = () => {
        const target = gameObject.hp;
        const delta = target - displayedHp;
        if (Math.abs(delta) < 0.5) {
            displayedHp = target;
            redraw();
            animFrame = null;
            return;
        }
        displayedHp += delta * 0.2;
        redraw();
        animFrame = requestAnimationFrame(animateHp);
    };

    const __update = () => {
        displayedHp = gameObject.hp;
        redraw();
    };
    __update();
    gameObject.on("name_changed", __update);
    gameObject.on("selected", __update);
    gameObject.on("deselected", __update);
    gameObject.on("hp_changed", () => {
        if (animFrame === null) {
            animFrame = requestAnimationFrame(animateHp);
        }
    });
    gameObject.on("hover_start", __update);
    gameObject.on("hover_end", __update);

    return texture;
};
